import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle, Send, X, Loader2, Trash2, 
  Bot, User, Wrench, CreditCard, HelpCircle,
  CheckCircle, AlertTriangle, XCircle, ChevronRight,
  UserPlus, Settings, RefreshCw, Database, Shield
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Department configurations
const DEPARTMENTS = {
  help: {
    id: 'help',
    name: 'Help Desk',
    description: 'General CRM help & navigation',
    icon: HelpCircle,
    color: 'emerald',
    bgColor: 'bg-emerald-500',
    lightBg: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    textColor: 'text-emerald-700'
  },
  it: {
    id: 'it',
    name: 'IT Support',
    description: 'Fix issues & run diagnostics',
    icon: Wrench,
    color: 'blue',
    bgColor: 'bg-blue-500',
    lightBg: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-700'
  },
  accounts: {
    id: 'accounts',
    name: 'Accounts',
    description: 'Manage workers & subscription',
    icon: CreditCard,
    color: 'purple',
    bgColor: 'bg-purple-500',
    lightBg: 'bg-purple-50',
    borderColor: 'border-purple-200',
    textColor: 'text-purple-700'
  }
};

// Action result component
const ActionResult = ({ result }) => {
  if (!result) return null;
  
  const isSuccess = result.success;
  const Icon = isSuccess ? CheckCircle : XCircle;
  
  return (
    <div className={`mt-2 p-2 rounded-lg text-sm ${isSuccess ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
      <div className="flex items-center gap-2">
        <Icon size={14} className={isSuccess ? 'text-green-600' : 'text-red-600'} />
        <span className={isSuccess ? 'text-green-700' : 'text-red-700'}>
          {result.message}
        </span>
      </div>
      {result.details && (
        <div className="mt-1 text-xs text-gray-600 pl-5">
          {result.details}
        </div>
      )}
    </div>
  );
};

// Pending action confirmation
const PendingAction = ({ action, onConfirm, onCancel, isExecuting }) => {
  return (
    <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className="text-amber-600 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-800">Confirm Action</p>
          <p className="text-sm text-amber-700 mt-1">{action.description}</p>
          {action.details && (
            <ul className="text-xs text-amber-600 mt-1 list-disc pl-4">
              {action.details.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          )}
          <div className="flex gap-2 mt-2">
            <Button 
              size="sm" 
              onClick={onConfirm}
              disabled={isExecuting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {isExecuting ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCircle size={14} className="mr-1" />}
              Yes, proceed
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={onCancel}
              disabled={isExecuting}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AIStaffAssistant = ({ onClose }) => {
  const [currentDept, setCurrentDept] = useState('help');
  const [messages, setMessages] = useState({});
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionIds, setSessionIds] = useState({});
  const [pendingAction, setPendingAction] = useState(null);
  const [isExecutingAction, setIsExecutingAction] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const dept = DEPARTMENTS[currentDept];
  const DeptIcon = dept.icon;

  // Initialize messages for each department
  useEffect(() => {
    if (!messages[currentDept]) {
      const welcomeMessages = {
        help: "Hi! I'm your Help Desk assistant. I can explain how to use any feature in StreakFree. What would you like to know?",
        it: "Hello! I'm IT Support. I can diagnose issues, run system checks, and apply fixes. What problem can I help you with?\n\nI can:\n• Run diagnostics on your account\n• Fix sync issues\n• Clear stuck jobs\n• Check system health",
        accounts: "Hi! I'm your Accounts manager. I can help with your subscription, add workers, and manage your team.\n\nI can:\n• Add new workers to your account\n• Update your user limit\n• Check your current plan & pricing\n• Activate/deactivate team members"
      };
      setMessages(prev => ({
        ...prev,
        [currentDept]: [{ role: 'assistant', content: welcomeMessages[currentDept] }]
      }));
    }
  }, [currentDept, messages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentDept]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => ({
      ...prev,
      [currentDept]: [...(prev[currentDept] || []), { role: 'user', content: userMessage }]
    }));
    setIsLoading(true);

    try {
      const token = localStorage.getItem('streakfree_token');
      
      if (!token) {
        setMessages(prev => ({
          ...prev,
          [currentDept]: [...(prev[currentDept] || []), { 
            role: 'assistant', 
            content: "You need to be logged in to use AI Staff. Please log in and try again." 
          }]
        }));
        return;
      }
      
      const response = await fetch(`${API_URL}/api/assistant/staff/${currentDept}/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionIds[currentDept]
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSessionIds(prev => ({ ...prev, [currentDept]: data.session_id }));
        
        // Check if there's a pending action
        if (data.pending_action) {
          setPendingAction(data.pending_action);
        }
        
        // Check if an action was executed
        const actionResult = data.action_result ? { 
          role: 'action', 
          result: data.action_result 
        } : null;
        
        setMessages(prev => ({
          ...prev,
          [currentDept]: [
            ...(prev[currentDept] || []), 
            { role: 'assistant', content: data.response },
            ...(actionResult ? [actionResult] : [])
          ]
        }));
      } else {
        let errorMessage = 'Please try again.';
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
        } catch {
          // If response is not JSON
        }
        
        // Handle specific error cases
        if (response.status === 401 || errorMessage.includes('token') || errorMessage.includes('auth')) {
          errorMessage = 'Your session has expired. Please log out and log back in.';
        } else if (response.status === 500 && errorMessage.includes('AI service')) {
          errorMessage = 'AI service is temporarily unavailable. Please try again in a moment.';
        }
        
        setMessages(prev => ({
          ...prev,
          [currentDept]: [...(prev[currentDept] || []), { 
            role: 'assistant', 
            content: `Sorry, I encountered an error: ${errorMessage}` 
          }]
        }));
      }
    } catch (error) {
      console.error('Chat error:', error);
      
      // Check if it's a network error
      let errorMessage = "Sorry, I couldn't connect to the server.";
      if (error.message && error.message.includes('Failed to fetch')) {
        errorMessage = "Network error - please check your internet connection and try again.";
      }
      
      setMessages(prev => ({
        ...prev,
        [currentDept]: [...(prev[currentDept] || []), { 
          role: 'assistant', 
          content: errorMessage 
        }]
      }));
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const confirmAction = async () => {
    if (!pendingAction) return;
    
    setIsExecutingAction(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/assistant/staff/${currentDept}/execute`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action_id: pendingAction.id,
          action_type: pendingAction.type,
          params: pendingAction.params
        })
      });

      const data = await response.json();
      
      setMessages(prev => ({
        ...prev,
        [currentDept]: [
          ...(prev[currentDept] || []),
          { role: 'action', result: { success: response.ok, message: data.message, details: data.details } }
        ]
      }));
    } catch (error) {
      setMessages(prev => ({
        ...prev,
        [currentDept]: [
          ...(prev[currentDept] || []),
          { role: 'action', result: { success: false, message: 'Failed to execute action' } }
        ]
      }));
    } finally {
      setIsExecutingAction(false);
      setPendingAction(null);
    }
  };

  const cancelAction = () => {
    setPendingAction(null);
    setMessages(prev => ({
      ...prev,
      [currentDept]: [
        ...(prev[currentDept] || []),
        { role: 'assistant', content: "No problem, I've cancelled that action. Is there anything else I can help with?" }
      ]
    }));
  };

  const clearChat = async () => {
    const sessionId = sessionIds[currentDept];
    if (sessionId) {
      try {
        const token = localStorage.getItem('streakfree_token');
        await fetch(`${API_URL}/api/staff/${currentDept}/chat/${sessionId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Error clearing chat:', error);
      }
    }
    setMessages(prev => ({ ...prev, [currentDept]: [] }));
    setSessionIds(prev => ({ ...prev, [currentDept]: null }));
    setPendingAction(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const currentMessages = messages[currentDept] || [];

  // Quick actions per department
  const quickActions = {
    help: ["How do I add a customer?", "Where are invoices?", "How to schedule jobs?"],
    it: ["Run diagnostics", "Check sync status", "Fix stuck jobs"],
    accounts: ["Add a worker", "What's my monthly cost?", "Increase user limit"]
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="w-full max-w-md h-[600px] modal-shell-h bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-scale-in"
        onClick={e => e.stopPropagation()}
        data-testid="ai-staff-assistant"
      >
        {/* Department Tabs */}
        <div className="bg-white border-b border-gray-200 p-2 flex gap-1">
          {Object.values(DEPARTMENTS).map((d) => {
            const Icon = d.icon;
            const isActive = currentDept === d.id;
            return (
              <button
                key={d.id}
                onClick={() => setCurrentDept(d.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-xs font-medium transition-all ${
                  isActive 
                    ? `${d.bgColor} text-white shadow-sm` 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`dept-tab-${d.id}`}
              >
                <Icon size={14} />
                <span>{d.name}</span>
              </button>
            );
          })}
        </div>

        {/* Header with Close button */}
        <div className={`${dept.bgColor} px-4 py-3 flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <DeptIcon size={18} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white text-sm">{dept.name}</h3>
              <p className="text-white/80 text-xs">{dept.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={clearChat}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              title="Clear chat"
              data-testid="clear-chat"
            >
              <Trash2 size={16} className="text-white/80" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              title="Close"
              data-testid="close-chat"
            >
              <X size={18} className="text-white" />
            </button>
          </div>
        </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {currentMessages.map((msg, idx) => {
          if (msg.role === 'action') {
            return <ActionResult key={idx} result={msg.result} />;
          }
          
          return (
            <div
              key={idx}
              className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
            >
              <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center ${
                msg.role === 'user' 
                  ? `${dept.bgColor} text-white` 
                  : 'bg-zinc-200 text-zinc-600'
              }`}>
                {msg.role === 'user' ? <User size={14} /> : <DeptIcon size={14} />}
              </div>
              <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                msg.role === 'user'
                  ? `${dept.bgColor} text-white`
                  : 'bg-white border border-zinc-200 text-zinc-700'
              }`}>
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          );
        })}
        
        {/* Pending Action Confirmation */}
        {pendingAction && (
          <PendingAction 
            action={pendingAction}
            onConfirm={confirmAction}
            onCancel={cancelAction}
            isExecuting={isExecutingAction}
          />
        )}
        
        {isLoading && (
          <div className="flex gap-2">
            <div className="w-7 h-7 rounded-full bg-zinc-200 flex items-center justify-center">
              <DeptIcon size={14} className="text-zinc-600" />
            </div>
            <div className="bg-white border border-zinc-200 rounded-xl px-3 py-2">
              <Loader2 size={16} className={`animate-spin ${dept.textColor}`} />
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Actions */}
      {currentMessages.length <= 1 && (
        <div className="px-3 pb-2">
          <p className="text-xs text-zinc-500 mb-2">Quick actions:</p>
          <div className="flex flex-wrap gap-1">
            {quickActions[currentDept].map((action, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setInput(action);
                  setTimeout(sendMessage, 100);
                }}
                className={`text-xs bg-white border ${dept.borderColor} px-2 py-1 rounded-full hover:${dept.lightBg} transition-colors`}
                data-testid={`quick-action-${idx}`}
              >
                {action}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-3 bg-white border-t border-zinc-200">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={`Ask ${dept.name}...`}
            className={`flex-1 px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-${dept.color}-500 focus:border-transparent`}
            disabled={isLoading || isExecutingAction}
            data-testid="chat-input"
          />
          <Button
            onClick={sendMessage}
            disabled={!input.trim() || isLoading || isExecutingAction}
            className={`${dept.bgColor} hover:opacity-90 px-3`}
            data-testid="send-message"
          >
            {isLoading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Send size={16} />
            )}
          </Button>
        </div>
      </div>
      </div>
      
      <style>{`
        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-scale-in {
          animation: scaleIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AIStaffAssistant;
